'use client';

import React from 'react';
import Link from 'next/link';
import { Eye, ArrowUpRight, Truck } from 'lucide-react';

// Backend integration: GET /api/seller/orders?limit=10&sort=created_at:desc
const recentOrders = [
  {
    id: 'ord-20260510-1842',
    customer: 'Bilal Hussain',
    city: 'Lahore',
    products: 2,
    date: '10 May 2026, 09:44',
    amount: 'PKR 1,07,999',
    payment: 'Online',
    status: 'pending' as const,
    fba: false,
  },
  {
    id: 'ord-20260510-1831',
    customer: 'Zara Malik',
    city: 'Karachi',
    products: 1,
    date: '10 May 2026, 09:12',
    amount: 'PKR 18,500',
    payment: 'COD',
    status: 'processing' as const,
    fba: false,
  },
  {
    id: 'ord-20260510-1819',
    customer: 'Usman Tariq',
    city: 'Islamabad',
    products: 3,
    date: '10 May 2026, 08:55',
    amount: 'PKR 2,31,000',
    payment: 'Online',
    status: 'processing' as const,
    fba: true,
  },
  {
    id: 'ord-20260510-1802',
    customer: 'Sana Iqbal',
    city: 'Faisalabad',
    products: 1,
    date: '10 May 2026, 08:22',
    amount: 'PKR 89,999',
    payment: 'Online',
    status: 'shipped' as const,
    fba: true,
  },
  {
    id: 'ord-20260509-1791',
    customer: 'Kamran Shah',
    city: 'Multan',
    products: 2,
    date: '09 May 2026, 22:41',
    amount: 'PKR 37,000',
    payment: 'COD',
    status: 'shipped' as const,
    fba: false,
  },
  {
    id: 'ord-20260509-1778',
    customer: 'Ayesha Raza',
    city: 'Rawalpindi',
    products: 1,
    date: '09 May 2026, 19:30',
    amount: 'PKR 62,000',
    payment: 'Online',
    status: 'delivered' as const,
    fba: false,
  },
  {
    id: 'ord-20260509-1765',
    customer: 'Hassan Mirza',
    city: 'Lahore',
    products: 4,
    date: '09 May 2026, 16:18',
    amount: 'PKR 3,58,000',
    payment: 'Online',
    status: 'delivered' as const,
    fba: true,
  },
  {
    id: 'ord-20260509-1754',
    customer: 'Nadia Farooq',
    city: 'Karachi',
    products: 1,
    date: '09 May 2026, 14:05',
    amount: 'PKR 1,24,500',
    payment: 'COD',
    status: 'cancelled' as const,
    fba: false,
  },
  {
    id: 'ord-20260509-1741',
    customer: 'Imran Baig',
    city: 'Sialkot',
    products: 2,
    date: '09 May 2026, 11:44',
    amount: 'PKR 44,000',
    payment: 'Online',
    status: 'return' as const,
    fba: false,
  },
  {
    id: 'ord-20260509-1728',
    customer: 'Fatima Zaidi',
    city: 'Lahore',
    products: 1,
    date: '09 May 2026, 09:22',
    amount: 'PKR 18,500',
    payment: 'Online',
    status: 'delivered' as const,
    fba: false,
  },
];

const statusMap = {
  pending: <span className="status-pending">Pending</span>,
  processing: <span className="status-processing">Processing</span>,
  shipped: <span className="status-shipped">Shipped</span>,
  delivered: <span className="status-delivered">Delivered</span>,
  cancelled: <span className="status-cancelled">Cancelled</span>,
  return: <span className="status-return">Return</span>,
  dispute: <span className="status-dispute">Dispute</span>,
};

export default function RecentOrdersTable() {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Truck size={16} className="text-primary" />
          <h2 className="text-base font-semibold">Recent Orders</h2>
          <span className="text-xs text-muted-foreground">(Latest 10)</span>
        </div>
        <Link href="/order-management" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
          Manage Orders <ArrowUpRight size={12} />
        </Link>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="border-b border-border">
              {['Order #', 'Customer', 'Items', 'Date', 'Amount', 'Payment', 'Status', ''].map((col, i) => (
                <th
                  key={`orh-${i}`}
                  className="pb-2 text-xs font-semibold text-muted-foreground text-left whitespace-nowrap pr-4 last:pr-0"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recentOrders.map((order) => (
              <tr key={order.id} className="table-row-hover border-b border-border/50 last:border-0 group">
                <td className="py-2.5 pr-4">
                  <div className="flex items-center gap-1.5">
                    <code className="text-xs font-mono text-primary">{order.id}</code>
                    {order.fba && (
                      <span className="text-2xs bg-primary text-white px-1 py-0.5 rounded font-bold">FBA</span>
                    )}
                  </div>
                </td>
                <td className="py-2.5 pr-4">
                  <p className="text-xs font-medium">{order.customer}</p>
                  <p className="text-2xs text-muted-foreground">{order.city}</p>
                </td>
                <td className="py-2.5 pr-4 text-xs text-center">
                  <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs font-medium">
                    {order.products}
                  </span>
                </td>
                <td className="py-2.5 pr-4 text-xs text-muted-foreground whitespace-nowrap">{order.date}</td>
                <td className="py-2.5 pr-4 text-xs font-bold tabular-nums whitespace-nowrap">{order.amount}</td>
                <td className="py-2.5 pr-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    order.payment === 'COD' ?'bg-orange-50 text-orange-700' :'bg-blue-50 text-blue-700'
                  }`}>
                    {order.payment}
                  </span>
                </td>
                <td className="py-2.5 pr-4">{statusMap[order.status]}</td>
                <td className="py-2.5">
                  <Link href="/order-management">
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity btn-ghost p-1" title="View order details">
                      <Eye size={14} />
                    </button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}